import { useEffect, useMemo, useState } from "react";
import { Bookmark, RotateCcw, Trash2, Plus, Camera } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

/**
 * Scenario Snapshots — save & compare "what-if" simulations for a single
 * Evaluation Sheet without touching the backend.
 *
 * Why localStorage and not the backend?
 * --------------------------------------
 * Scenarios are intrinsically personal scratchpad data. Persisting them
 * server-side would require auth + a new collection + sync logic and
 * the user already has the canonical analysis saved via the share link.
 * Keeping snapshots client-side ships immediately, works for guests too,
 * and survives page reloads — which is the only durability investors
 * actually need while iterating on assumptions.
 *
 * Keying: `dr_snapshots_<analysisId>` so each property has its own list.
 */

const COPY = {
  it: {
    eyebrow: "Salva simulazioni",
    title: "Confronta più ipotesi sullo stesso immobile",
    sub: "Salva uno snapshot dei parametri attuali (spese condominiali, tasso mutuo, anticipo, durata) per confrontarlo con altri scenari. Massimo 5 simulazioni per immobile.",
    savePlaceholder: "Es. Mutuo aggressivo · anticipo 30%",
    saveBtn: "Salva snapshot",
    empty: "Nessuno snapshot salvato per questa analisi. Modifica i parametri qui sopra e premi \"Salva snapshot\" per congelare l'ipotesi.",
    restore: "Applica",
    delete: "Elimina",
    cols: {
      name: "Etichetta",
      condo: "Spese condo",
      rate: "Tasso",
      down: "Anticipo",
      years: "Durata",
      monthly: "Rata/mese",
      cashflow: "Cashflow",
      netYield: "Net yield",
    },
    condoYes: "Previste",
    condoNo: "Non previste",
    years: "anni",
    full: "Hai raggiunto il limite di 5 snapshot. Elimina un'ipotesi per salvarne una nuova.",
    saved: "Snapshot salvato",
    restored: "Snapshot applicato",
    removed: "Snapshot eliminato",
    untitled: "Snapshot",
  },
  en: {
    eyebrow: "Save simulations",
    title: "Compare multiple assumptions on the same property",
    sub: "Save a snapshot of the current parameters (condo fees, mortgage rate, down payment, term) to compare it with other scenarios. Up to 5 simulations per property.",
    savePlaceholder: "E.g. Aggressive mortgage · 30% down",
    saveBtn: "Save snapshot",
    empty: "No snapshots saved for this analysis yet. Tweak the parameters above and hit \"Save snapshot\" to freeze the assumption.",
    restore: "Apply",
    delete: "Delete",
    cols: {
      name: "Label",
      condo: "Condo fees",
      rate: "Rate",
      down: "Down",
      years: "Term",
      monthly: "Monthly",
      cashflow: "Cashflow",
      netYield: "Net yield",
    },
    condoYes: "Included",
    condoNo: "Excluded",
    years: "yrs",
    full: "You've reached the 5-snapshot limit. Delete one to save a new assumption.",
    saved: "Snapshot saved",
    restored: "Snapshot applied",
    removed: "Snapshot deleted",
    untitled: "Snapshot",
  },
};

const MAX_SNAPSHOTS = 5;
const STORAGE_PREFIX = "dr_snapshots_";

const fmtEur = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(Math.round(n));

const fmtPct = (n) => (n == null ? "—" : `${n.toFixed(2)}%`);

function monthlyPayment(principal, annualRatePct, years) {
  if (principal <= 0) return 0;
  if (years <= 0) return principal;
  const i = annualRatePct / 100 / 12;
  const n = years * 12;
  if (i === 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
}

const loadSnapshots = (analysisId) => {
  if (!analysisId || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + analysisId);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persistSnapshots = (analysisId, list) => {
  if (!analysisId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + analysisId, JSON.stringify(list));
  } catch {
    /* quota / private mode — fail silently */
  }
};

/**
 * Compute the derived metrics for a given snapshot. We keep stored data
 * minimal (just the user-chosen inputs) and re-derive cashflow/yield on
 * render so any later refinement to formulas applies retroactively.
 */
function deriveMetrics({ price, rental, netYield, snapshot }) {
  const { condoIncluded, rate, down, years } = snapshot;
  const baseNet = rental?.longterm?.annual_net_eur ?? 0;
  // Condo annual saving (mid-point) only counts when user excluded them.
  const condoMidpoint = netYield?.cost_condo_eur ?? 0;
  const condoSaving = condoIncluded ? 0 : condoMidpoint;
  const annualIncome = baseNet + condoSaving;

  let monthly = null;
  let annualPayment = null;
  let cashflow = null;
  if (price && price > 0) {
    const principal = Math.max(0, price * (1 - (down ?? 0) / 100));
    monthly = monthlyPayment(principal, rate ?? 0, years ?? 0);
    annualPayment = monthly * 12;
    cashflow = annualIncome - annualPayment;
  }

  // Combined net yield (uses the same logic as NetYieldBox).
  let combinedNetYieldPct = null;
  if (netYield && price > 0) {
    const totalCosts =
      (netYield.cost_imu_eur || 0) +
      (condoIncluded ? netYield.cost_condo_eur || 0 : 0) +
      (netYield.cost_maintenance_eur || 0) +
      (netYield.cost_vacancy_eur || 0) +
      (netYield.cost_management_eur || 0);
    const annualNet = (netYield.annual_gross_eur || 0) - totalCosts;
    combinedNetYieldPct = (annualNet / price) * 100;
  }

  return { monthly, annualPayment, cashflow, combinedNetYieldPct };
}

const ScenarioSnapshots = ({
  analysisId,
  condoIncluded,
  setCondoIncluded,
  rate,
  setRate,
  down,
  setDown,
  years,
  setYears,
  price,
  rental,
  netYield,
  hasMortgage,
}) => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.it;
  const [snapshots, setSnapshots] = useState([]);
  const [label, setLabel] = useState("");

  // Hydrate on mount / when the analysis changes.
  useEffect(() => {
    setSnapshots(loadSnapshots(analysisId));
  }, [analysisId]);

  const persist = (next) => {
    setSnapshots(next);
    persistSnapshots(analysisId, next);
  };

  const handleSave = () => {
    if (snapshots.length >= MAX_SNAPSHOTS) {
      toast.error(c.full);
      return;
    }
    const trimmed = label.trim();
    const snap = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: trimmed || `${c.untitled} ${snapshots.length + 1}`,
      condoIncluded,
      rate,
      down,
      years,
      created_at: new Date().toISOString(),
    };
    persist([snap, ...snapshots]);
    setLabel("");
    toast.success(c.saved);
    trackEvent("scenario_snapshot_saved", {
      analysis_id: analysisId,
      condo_included: condoIncluded,
      rate,
      down,
      years,
      total_snapshots: snapshots.length + 1,
    });
  };

  const handleRestore = (snap) => {
    setCondoIncluded(snap.condoIncluded);
    if (typeof setRate === "function") setRate(snap.rate);
    if (typeof setDown === "function") setDown(snap.down);
    if (typeof setYears === "function") setYears(snap.years);
    toast.success(c.restored);
    trackEvent("scenario_snapshot_restored", {
      analysis_id: analysisId,
      snapshot_id: snap.id,
    });
  };

  const handleDelete = (id) => {
    persist(snapshots.filter((s) => s.id !== id));
    toast.success(c.removed);
  };

  // Derived metrics per snapshot, memoised.
  const rows = useMemo(
    () =>
      snapshots.map((s) => ({
        snap: s,
        metrics: deriveMetrics({ price, rental, netYield, snapshot: s }),
      })),
    [snapshots, price, rental, netYield]
  );

  // Only render when there is at least mortgage OR net-yield context to
  // snapshot. If both are missing the section adds no value.
  if (!analysisId || (!hasMortgage && !netYield)) return null;

  return (
    <section
      data-testid="scenario-snapshots"
      className="px-6 md:px-10 py-8 border-t border-[#E6E4DD] bg-[#FAF9F4]"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Camera size={12} strokeWidth={1.5} />
            {c.eyebrow}
          </div>
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#282C2A] mt-2">
            {c.title}
          </h3>
          <p className="text-sm text-[#6C706D] mt-1 max-w-2xl">{c.sub}</p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C706D]">
          {snapshots.length}/{MAX_SNAPSHOTS}
        </div>
      </div>

      {/* Save row */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={48}
          placeholder={c.savePlaceholder}
          data-testid="snapshot-label-input"
          className="flex-1 border border-[#E6E4DD] bg-white px-3 py-2.5 text-sm font-mono text-[#282C2A] placeholder:text-[#A6AAA7] focus:outline-none focus:border-[#4A5D23] transition-colors"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={snapshots.length >= MAX_SNAPSHOTS}
          data-testid="snapshot-save-btn"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#4A5D23] text-white font-semibold tracking-wider uppercase text-xs hover:bg-[#3B4A1C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Bookmark size={14} strokeWidth={1.75} />
          {c.saveBtn}
        </button>
      </div>

      {snapshots.length === 0 ? (
        <p
          data-testid="snapshot-empty"
          className="text-xs text-[#6C706D] italic border border-dashed border-[#E6E4DD] bg-white px-4 py-6 text-center"
        >
          {c.empty}
        </p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table
            data-testid="snapshot-table"
            className="w-full text-xs font-mono min-w-[760px]"
          >
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#6C706D] border-b border-[#E6E4DD]">
                <th className="px-2 py-2 font-normal">{c.cols.name}</th>
                <th className="px-2 py-2 font-normal">{c.cols.condo}</th>
                <th className="px-2 py-2 font-normal text-right">{c.cols.rate}</th>
                <th className="px-2 py-2 font-normal text-right">{c.cols.down}</th>
                <th className="px-2 py-2 font-normal text-right">{c.cols.years}</th>
                <th className="px-2 py-2 font-normal text-right">{c.cols.monthly}</th>
                <th className="px-2 py-2 font-normal text-right">{c.cols.cashflow}</th>
                <th className="px-2 py-2 font-normal text-right">{c.cols.netYield}</th>
                <th className="px-2 py-2 font-normal text-right" />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ snap, metrics }) => {
                const cfPositive = (metrics.cashflow ?? 0) >= 0;
                return (
                  <tr
                    key={snap.id}
                    data-testid={`snapshot-row-${snap.id}`}
                    className="border-b border-[#EFEDE6] hover:bg-white transition-colors"
                  >
                    <td className="px-2 py-3 text-[#282C2A] font-sans text-sm max-w-[180px] truncate">
                      {snap.name}
                    </td>
                    <td className="px-2 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border ${
                          snap.condoIncluded
                            ? "border-[#4A5D23] text-[#4A5D23]"
                            : "border-[#A6AAA7] text-[#6C706D] line-through"
                        }`}
                      >
                        {snap.condoIncluded ? c.condoYes : c.condoNo}
                      </span>
                    </td>
                    <td className="px-2 py-3 text-right text-[#282C2A]">
                      {snap.rate?.toFixed(1)}%
                    </td>
                    <td className="px-2 py-3 text-right text-[#282C2A]">{snap.down}%</td>
                    <td className="px-2 py-3 text-right text-[#282C2A]">
                      {snap.years} {c.years}
                    </td>
                    <td className="px-2 py-3 text-right text-[#8B2E1F]">
                      {fmtEur(metrics.monthly)}
                    </td>
                    <td
                      className={`px-2 py-3 text-right font-semibold ${
                        cfPositive ? "text-[#4A5D23]" : "text-[#8B2E1F]"
                      }`}
                    >
                      {metrics.cashflow == null
                        ? "—"
                        : `${cfPositive ? "+" : ""}${fmtEur(metrics.cashflow)}`}
                    </td>
                    <td className="px-2 py-3 text-right text-[#4A5D23]">
                      {fmtPct(metrics.combinedNetYieldPct)}
                    </td>
                    <td className="px-2 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleRestore(snap)}
                        data-testid={`snapshot-restore-${snap.id}`}
                        title={c.restore}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23] hover:text-white transition-colors mr-1"
                      >
                        <RotateCcw size={11} strokeWidth={1.75} />
                        {c.restore}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(snap.id)}
                        data-testid={`snapshot-delete-${snap.id}`}
                        title={c.delete}
                        aria-label={c.delete}
                        className="inline-flex items-center justify-center w-7 h-7 text-[#A6AAA7] hover:text-[#8B2E1F] transition-colors"
                      >
                        <Trash2 size={13} strokeWidth={1.75} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default ScenarioSnapshots;
